///
/// Copyright (c) 2017-present, Siklu Ltd.
/// All rights reserved.
///

import { Injectable, NgZone, SecurityContext } from '@angular/core';
import { Subject } from 'rxjs';
import { DomSanitizer } from '@angular/platform-browser';
import { SuiTranslateService } from '@siklu/sui-lib/localization';

export type GtthAlertType = 'success' | 'danger' | 'warning' | 'info';

export interface GtthAlert {
    id?: number;
    type: GtthAlertType;
    msg: string;
    params?: any;
    timeout?: number;
    toast?: boolean;
    position?: string;
    scoped?: boolean;
    close?: (alerts: GtthAlert[]) => void;
}

@Injectable({
    providedIn: 'root'
})
export class GtthAlertService {
    private alertId: number;
    private readonly alerts: GtthAlert[];
    private readonly timeout: number;
    private readonly toast: boolean;
    private readonly i18nEnabled: boolean;

    private readonly showAlertSource = new Subject<string | string[] | { msg: string | string[]; data: any }>();
    public showAlertSource$ = this.showAlertSource.asObservable();

    constructor(
        private readonly sanitizer: DomSanitizer,
        private readonly translateService: SuiTranslateService,
        private readonly ngZone: NgZone
    ) {
        const config = {
            alertAsToast: false,
            i18nEnabled: false,
            defaultI18nLang: 'en'
        };
        this.toast = config.alertAsToast;
        this.i18nEnabled = config.i18nEnabled;
        this.alertId = 0; // unique id for each alert. Starts from 0.
        this.alerts = [];
        this.timeout = 5000;
    }

    clear() {
        this.alerts.splice(0, this.alerts.length);
    }

    get(): GtthAlert[] {
        return this.alerts;
    }

    success(msg: string, params?: any, position?: string): GtthAlert {
        return this.addAlert(
            {
                type: 'success',
                msg,
                params,
                timeout: this.timeout,
                toast: this.isToast(),
                position
            },
            []
        );
    }

    error(msg: string, params?: any, position?: string): GtthAlert {
        return this.addAlert(
            {
                type: 'danger',
                msg,
                params,
                timeout: this.timeout,
                toast: this.isToast(),
                position
            },
            []
        );
    }

    warning(msg: string, params?: any, position?: string): GtthAlert {
        return this.addAlert(
            {
                type: 'warning',
                msg,
                params,
                timeout: this.timeout,
                toast: this.isToast(),
                position
            },
            []
        );
    }

    info(msg: string, params?: any, position?: string): GtthAlert {
        return this.addAlert(
            {
                type: 'info',
                msg,
                params,
                timeout: this.timeout,
                toast: this.isToast(),
                position
            },
            []
        );
    }

    private factory(alertOptions: GtthAlert): GtthAlert {
        const alert: GtthAlert = {
            type: alertOptions.type,
            msg: this.sanitizer.sanitize(SecurityContext.HTML, alertOptions.msg),
            id: alertOptions.id,
            timeout: alertOptions.timeout,
            toast: alertOptions.toast,
            position: alertOptions.position ? alertOptions.position : 'top right',
            scoped: alertOptions.scoped,
            close: (alerts: GtthAlert[]) => {
                return this.closeAlert(alertOptions.id, alerts);
            }
        };
        if (!alert.scoped) {
            this.alerts.push(alert);
        }
        return alert;
    }

    addAlert(alertOptions: GtthAlert, extAlerts: GtthAlert[]): GtthAlert {
        alertOptions.id = this.alertId;
        this.alertId++;

        if (this.i18nEnabled && alertOptions.msg) {
            alertOptions.msg = this.translateService.translate(alertOptions.msg, alertOptions.params);
        }
        const alert = this.factory(alertOptions);
        if (alertOptions.timeout && alertOptions.timeout > 0) {
            this.ngZone.runOutsideAngular(() => {
                setTimeout(() => {
                    this.ngZone.run(() => {
                        this.closeAlert(alertOptions.id, extAlerts);
                    });
                }, alertOptions.timeout);
            });
        }
        return alert;
    }

    closeAlert(id: number, extAlerts?: GtthAlert[]): any {
        const thisAlerts: GtthAlert[] = extAlerts && extAlerts.length > 0 ? extAlerts : this.alerts;
        return this.closeAlertByIndex(thisAlerts.map((e) => e.id).indexOf(id), thisAlerts);
    }

    closeAlertByIndex(index: number, thisAlerts: GtthAlert[]): GtthAlert[] {
        return thisAlerts.splice(index, 1);
    }

    isToast(): boolean {
        return this.toast;
    }

    public showAlert(message: string | string[] | { msg: string | string[]; data: any }) {
        if (typeof message === 'object' && Object.keys(message).length && !Array.isArray(message)) {
            this.showAlertSource.next({ msg: message.msg, data: message.data });
        } else {
            this.showAlertSource.next(message);
        }
    }
}
